
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { replace } from 'react-router-redux';
import {
  Button,
  Container,
  Dimmer,
  Dropdown,
  Grid,
  Loader,
  Menu,
} from 'semantic-ui-react';
import { flashSuccessMessage, flashErrorMessage } from '../../actions/flash';
import { fetchM } from '../../actions/fetchM';
import NavBar from '../../components/NavBar';
import SpecificationTable from '../../components/SpecificationTable';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

class CustomOptions extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.object,
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    t: PropTypes.func,
  }
  constructor(props) {
    super(props);
    this.state = {
      token: null,
      loading: true,
      data: null,
      oriData: null,
      isEditing: false,
      topMenuIsTab: window.innerWidth <= 590,
    };
  }
  componentDidMount() {
    this.fetchCustomOptions();
    window.addEventListener('resize', this.handleResize, false);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, false);
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchCustomOptions() {
    await this.setLoading(true);
    const { t } = this.props;
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM('/custom_options/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const data = await res.json();
      const token = res.headers.get('X-Csrf-Token');
      // handle token.. etc
      await this.setComponentState({
        token,
        data,
        oriData: data,
      });
    }
    await this.setLoading(false);
  }
  async updateCustomOptions() {
    await this.setLoading(true);
    const { t } = this.props;
    const { store } = this.context;
    let data = this.state.data || [];
    data = data.filter(d => d.name !== '');
    try {
      await store.dispatch(fetchM('/custom_options/', {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.state.token,
        },
        body: JSON.stringify(data),
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: t('There was an error with your request. Please try again.', { ns: TRANSLATION_FILES.translation }) }));
      return;
    }
    await this.setComponentState({
      isEditing: false,
    });
    await this.setLoading(false);
    this.fetchCustomOptions();
    store.dispatch(flashSuccessMessage({ header: t('Update Success', { ns: TRANSLATION_FILES.translation }), message: t('Custom options successfully updated.', { ns: TRANSLATION_FILES.translation }) }));
  }
  handleOnChange = (data) => {
    this.setState({ data });
  }
  handleOnSave = (e) => {
    e.preventDefault();
    if (JSON.stringify(this.state.data) !== JSON.stringify(this.state.oriData)) {
      this.updateCustomOptions();
    } else {
      this.setState({
        isEditing: false,
      });
    }
  }
  handleOnEdit = (e) => {
    e.preventDefault();
    this.setState({ isEditing: true });
  }
  handleMenu = (e, { value }) => {
    const { store } = this.context;
    store.dispatch(replace(value));
  }
  handleResize = () => {
    const state = {};
    const topMenuIsTab = window.innerWidth <= 590;
    if (topMenuIsTab !== this.state.topMenuIsTab) {
      state.topMenuIsTab = topMenuIsTab;
    }
    this.setState({ ...state });
  }
  render() {
    const { data, isEditing, loading } = this.state;
    const { pathname } = this.props.location;
    const { t } = this.props;
    const links = [
      { text: 'System Settings', value: '/settings/system_settings/' },
      { text: 'Custom Options', value: '/settings/custom_options/' },
    ];
    return (
      <Container>
        <NavBar
          links={
            this.state.topMenuIsTab ?
              <Dropdown
                item
                selectOnBlur={false}
                options={links}
                onChange={this.handleMenu}
                value={pathname}
              />
              : links.map((link, key) => (
                <Menu.Item
                  key={key}
                  name={t(link.text, { ns: TRANSLATION_FILES.screens })}
                  active={pathname === link.value}
                  onClick={e => this.handleMenu(e, link)}
                />
              ))
          }
          rightNav={
            <Menu.Item fitted>
              {isEditing ?
                <Button
                  color="green"
                  content={t('Save', { ns: TRANSLATION_FILES.translation })}
                  disabled={this.state.loading}
                  onClick={this.handleOnSave}
                /> :
                <Button
                  icon="edit"
                  content={t('Edit', { ns: TRANSLATION_FILES.translation })}
                  disabled={this.state.loading}
                  onClick={this.handleOnEdit}
                />
              }
            </Menu.Item>
          }
        />
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <Dimmer
                inverted
                active={this.state.loading}
              >
                <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
              </Dimmer>
              <SpecificationTable
                type="customOptions"
                editable
                isEditing={isEditing}
                hideButton
                notFoundNode={<p>{t('No options.', { ns: TRANSLATION_FILES.translation })}</p>}
                placeholderName={t('Enter option name', { ns: TRANSLATION_FILES.translation })}
                placeholderDescription={t('Enter option value', { ns: TRANSLATION_FILES.translation })}
                headerNode={t('Custom Options', { ns: TRANSLATION_FILES.screens })}
                data={data}
                onChange={this.handleOnChange}
                loading={loading}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(CustomOptions);



// WEBPACK FOOTER //
// ./src/containers/CustomOptions/CustomOptions.jsx